import { isbot } from 'isbot';
import { onCLS, onLCP, onTTFB, type Metric } from 'web-vitals';
// INP uses the attribution build so each report includes interactionTarget,
// loadState, and (Chrome 123+) the longest LoAF script. Without this we cannot
// see WHICH handler is slow on real devices — measured /es-MX/ p75 was 200 ms
// across 392 samples (analytics MCP, 28 d) but the basic web-vitals payload
// has no field that identifies the interaction target.
// LCP/CLS/TTFB stay on the basic build to keep their bundle slice small.
import { onINP, type INPMetricWithAttribution } from 'web-vitals/attribution';
import { getSessionId } from './session';
import { getAnalyticsEndpoint } from './hostUrl';

/**
 * Configuration for the analytics client
 */
interface AnalyticsConfig {
  source: string;
  debug?: boolean;
}

/**
 * Base event properties that can be tracked
 */
interface EventProperties {
  [key: string]: string | number | boolean | undefined | null;
}

/**
 * Properties specific to page view events
 */
interface PageViewProperties extends EventProperties {
  title?: string;
  referrer?: string;
}

/**
 * Properties for button click tracking
 */
interface ButtonClickProperties extends EventProperties {
  targetUrl?: string;
}

/**
 * Properties for navigation click tracking
 */
interface NavigationClickProperties extends EventProperties {
  section: 'menu' | 'footer' | string;
  label?: string;
  path?: string;
}

/**
 * UTM parameters captured from URL
 */
interface UTMParameters {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

/**
 * Enriched properties added to all events
 */
interface EnrichedProperties extends EventProperties {
  sessionId: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  browser: 'firefox' | 'chrome' | 'safari' | 'edge' | 'other' | 'unknown';
  locale: string;
  page: string;
  timestamp: string;
}

/**
 * Payload sent to the analytics backend
 */
interface AnalyticsPayload extends EnrichedProperties {
  eventName: string;
  _source: string;
}

/**
 * Response from the analytics backend
 */
interface AnalyticsResponse {
  success?: boolean;
  eventId?: string;
}

/**
 * Base64 encode a payload for analytics backend
 * The backend expects { data: "base64EncodedJSON" } for payload obscurity (not security)
 */
function encodePayload(payload: AnalyticsPayload): string {
  const jsonString = JSON.stringify(payload);
  // Use browser's btoa for Base64 encoding, fallback to Buffer for SSR
  if (typeof btoa !== 'undefined') {
    return btoa(jsonString);
  } else if (typeof Buffer !== 'undefined') {
    return Buffer.from(jsonString).toString('base64');
  }
  // Fallback for environments without btoa or Buffer
  throw new Error('Base64 encoding not available');
}

/**
 * Lightweight analytics client for Gatsby marketing site
 *
 * Features:
 * - Session management (30-minute timeout)
 * - Device/browser detection
 * - Environment-aware API endpoint
 * - Fire-and-forget pattern (never throws)
 * - Base64 payload encoding for backend compatibility
 */
export class GatsbyAnalyticsClient {
  private config: AnalyticsConfig;
  private _warnedLocalhostCors?: boolean;

  constructor(config: AnalyticsConfig) {
    this.config = config;
  }

  /**
   * Track generic event
   */
  trackEvent(eventType: string, properties?: EventProperties): void {
    try {
      const enriched = this.enrichProperties(properties || {});
      this.sendEvent(eventType, enriched);
    } catch (error) {
      if (this.config.debug) {
        console.error('[Analytics] trackEvent failed:', error);
      }
    }
  }

  /**
   * Track page view (called automatically via gatsby-browser.js)
   */
  trackPageView(page: string, properties?: PageViewProperties): void {
    try {
      const enriched = this.enrichProperties({
        ...properties,
        page,
        title:
          properties?.title ||
          (typeof document !== 'undefined' ? document.title : ''),
        referrer:
          typeof document !== 'undefined' ? document.referrer : undefined,
        ...this.captureUTMParameters(),
      });

      this.sendEvent('page.view', enriched);
    } catch (error) {
      if (this.config.debug) {
        console.error('[Analytics] trackPageView failed:', error);
      }
    }
  }

  /**
   * Track button click with full context for funnel analysis
   */
  trackButtonClick(buttonId: string, properties?: ButtonClickProperties): void {
    try {
      const enriched = this.enrichProperties({
        ...properties,
        buttonId,
        // Add context for debugging signup funnel drop-offs
        pathname: typeof window !== 'undefined' ? window.location.pathname : '',
        referrer: typeof document !== 'undefined' ? document.referrer : '',
        targetUrl: properties?.targetUrl || '',
        isAuthenticated: this.checkAuthenticationState(),
        ...this.captureUTMParameters(),
      });

      this.sendEvent('cta.click.signup', enriched);
    } catch (error) {
      if (this.config.debug) {
        console.error('[Analytics] trackButtonClick failed:', error);
      }
    }
  }

  /**
   * Check if user appears to be authenticated (has auth-related cookies)
   */
  checkAuthenticationState(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }
    // Check for common auth cookie patterns
    const cookies = document.cookie || '';
    return (
      cookies.includes('token=') ||
      cookies.includes('jwt=') ||
      cookies.includes('auth=') ||
      cookies.includes('mtp_session=')
    );
  }

  /**
   * Track navigation click
   */
  trackNavigationClick(properties: NavigationClickProperties): void {
    try {
      const enriched = this.enrichProperties(properties);
      const eventType =
        properties.section === 'menu'
          ? 'navigation.menu.click'
          : 'navigation.footer.click';
      this.sendEvent(eventType, enriched);
    } catch (error) {
      if (this.config.debug) {
        console.error('[Analytics] trackNavigationClick failed:', error);
      }
    }
  }

  /**
   * Enrich event properties with session, device, browser, locale
   */
  enrichProperties(properties: EventProperties): EnrichedProperties {
    return {
      ...properties,
      sessionId: getSessionId(),
      deviceType: this.getDeviceType(),
      browser: this.getBrowser(),
      locale: typeof navigator !== 'undefined' ? navigator.language : 'en-US',
      page: typeof window !== 'undefined' ? window.location.pathname : '',
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Send event to backend via REST endpoint
   * Uses environment-aware endpoint construction (hostUrl helper)
   * Encodes payload as Base64 to match backend expectations
   */
  private sendEvent(eventType: string, properties: EnrichedProperties): void {
    if (typeof window === 'undefined') {
      return;
    }

    // Skip tracking for known bots (Googlebot, Bingbot, etc.)
    // This prevents ~1,000+ bot events/month from being recorded
    if (typeof navigator !== 'undefined' && isbot(navigator.userAgent)) {
      if (this.config.debug) {
        console.log(
          '[Analytics] Skipping bot:',
          navigator.userAgent.substring(0, 60)
        );
      }
      return;
    }

    // Build REST payload - flatten properties to root level
    const payload: AnalyticsPayload = {
      eventName: eventType,
      timestamp: new Date().toISOString(),
      ...properties,
      _source: this.config.source,
    };

    // Use environment-aware endpoint instead of hardcoded URL
    const endpoint = getAnalyticsEndpoint();

    try {
      // Avoid noisy CORS failures in local dev when endpoint points to a different localhost origin.
      // (e.g. marketing site on :8000, analytics backend on :3000 without CORS configured)
      try {
        const endpointUrl = new URL(endpoint, window.location.origin);
        const isLocalHost = (host: string): boolean =>
          host === 'localhost' || host === '127.0.0.1';
        if (
          isLocalHost(window.location.hostname) &&
          isLocalHost(endpointUrl.hostname) &&
          endpointUrl.origin !== window.location.origin
        ) {
          if (this.config.debug && !this._warnedLocalhostCors) {
            console.warn(
              '[Analytics] Skipping event (cross-origin localhost endpoint):',
              endpointUrl.origin
            );
            this._warnedLocalhostCors = true;
          }
          return;
        }
      } catch {
        // If URL parsing fails, fall back to sending.
      }

      // Encode payload as Base64 - backend expects { data: "base64EncodedJSON" }
      const encodedData = encodePayload(payload);

      fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: encodedData }),
      })
        .then((response) => {
          if (!response.ok && this.config.debug) {
            console.warn('[Analytics] REST request failed:', response.status);
          }
          return response.json() as Promise<AnalyticsResponse>;
        })
        .then((data) => {
          if (this.config.debug && data.success) {
            console.log('[Analytics] Event tracked:', data.eventId);
          }
        })
        .catch((error: unknown) => {
          if (this.config.debug) {
            console.error('[Analytics] Network error:', error);
          }
        });
    } catch (error) {
      if (this.config.debug) {
        console.error('[Analytics] Encoding error:', error);
      }
    }
  }

  /**
   * Capture UTM parameters from URL
   */
  captureUTMParameters(): UTMParameters {
    if (typeof window === 'undefined') {
      return {};
    }

    const params = new URLSearchParams(window.location.search);
    return {
      utmSource: params.get('utm_source') || undefined,
      utmMedium: params.get('utm_medium') || undefined,
      utmCampaign: params.get('utm_campaign') || undefined,
    };
  }

  /**
   * Detect device type based on screen width
   */
  getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    if (typeof window === 'undefined') {
      return 'desktop';
    }

    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  /**
   * Detect browser from user agent
   */
  getBrowser(): 'firefox' | 'chrome' | 'safari' | 'edge' | 'other' | 'unknown' {
    if (typeof navigator === 'undefined') {
      return 'unknown';
    }

    const ua = navigator.userAgent;
    if (ua.includes('Firefox')) return 'firefox';
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'chrome';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'safari';
    if (ua.includes('Edg')) return 'edge';
    return 'other';
  }

  /**
   * Get current session ID (for passing to app via URL)
   */
  getSessionId(): string {
    return getSessionId();
  }

  /**
   * Track Core Web Vitals performance metrics
   * Captures LCP, CLS, TTFB, INP for diagnosing bounce causes.
   *
   * When the metric is INP from the attribution build, also extracts
   * interactionTarget / loadState / sub-part timings so we can pinpoint the
   * slow handler in the analytics database (see /es-MX/ p75=200 ms findings).
   */
  trackPerformance(metric: Metric | INPMetricWithAttribution): void {
    try {
      const baseProps: EventProperties = {
        metricName: metric.name,
        metricValue: Math.round(metric.value),
        metricRating: metric.rating, // 'good', 'needs-improvement', 'poor'
        metricDelta: Math.round(metric.delta),
        metricId: metric.id,
        navigationType:
          typeof performance !== 'undefined'
            ? (
                performance.getEntriesByType?.(
                  'navigation'
                )?.[0] as PerformanceNavigationTiming
              )?.type
            : undefined,
      };

      if (metric.name === 'INP' && 'attribution' in metric) {
        Object.assign(baseProps, this.extractInpAttribution(metric));
      }

      this.sendEvent('performance.web_vital', this.enrichProperties(baseProps));
    } catch (error) {
      if (this.config.debug) {
        console.error('[Analytics] trackPerformance failed:', error);
      }
    }
  }

  /**
   * Flatten INP attribution into scalar fields the analytics payload accepts.
   * Truncates the interaction target selector to keep payload size sane.
   */
  private extractInpAttribution(
    metric: INPMetricWithAttribution
  ): EventProperties {
    const a = metric.attribution;
    const out: EventProperties = {
      inpInteractionTarget: a.interactionTarget?.slice(0, 200) || undefined,
      inpInteractionType: a.interactionType,
      inpInputDelay: Math.round(a.inputDelay),
      inpProcessingDuration: Math.round(a.processingDuration),
      inpPresentationDelay: Math.round(a.presentationDelay),
      inpLoadState: a.loadState,
    };

    // Longest script in the LoAF window — Chrome 123+. Web-vitals already
    // pre-computes this and tags which INP subpart it ran in, so we just
    // forward the relevant fields. Field is undefined when no LoAF data is
    // available (older Chrome, very short interactions, no scripts > 5 ms).
    if (a.longestScript) {
      const { entry, subpart, intersectingDuration } = a.longestScript;
      out.inpLongestScriptUrl = entry.sourceURL?.slice(0, 200) || undefined;
      out.inpLongestScriptDuration = Math.round(entry.duration);
      out.inpLongestScriptInvoker = entry.invoker?.slice(0, 200) || undefined;
      out.inpLongestScriptInvokerType = entry.invokerType;
      out.inpLongestScriptSubpart = subpart;
      out.inpLongestScriptIntersecting = Math.round(intersectingDuration);
    }
    if (a.totalScriptDuration !== undefined) {
      out.inpTotalScriptDuration = Math.round(a.totalScriptDuration);
    }
    if (a.totalStyleAndLayoutDuration !== undefined) {
      out.inpTotalStyleLayoutDuration = Math.round(
        a.totalStyleAndLayoutDuration
      );
    }
    return out;
  }

  /**
   * Initialize Core Web Vitals tracking using web-vitals library
   * Tracks: LCP, CLS, TTFB, INP (FID deprecated in favor of INP)
   */
  initPerformanceTracking(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const trackMetric = (metric: Metric): void => this.trackPerformance(metric);
    const trackInp = (metric: INPMetricWithAttribution): void =>
      this.trackPerformance(metric);

    onLCP(trackMetric);
    onCLS(trackMetric);
    onTTFB(trackMetric);
    onINP(trackInp);
  }
}
